import { LngLat, LngLatBounds } from 'maplibre-gl';
import type { MapRef } from 'react-map-gl/maplibre';
import L, { type LatLngBounds, type FitBoundsOptions } from 'leaflet';
import { inRange } from 'lodash-es';
import type { BoundsLocation } from '#/types/BoundsLocation';
import featureFlags from '#/shared/feature-flags';
import MAP_CONFIG from '#/map-config';

const { mapLibre } = featureFlags;
const { poiBoundsExtent } = MAP_CONFIG;

type LeafletOrMapLibreBounds = BoundsLocation[] | LngLatBounds | LatLngBounds;

/**
 * Checks if the point is in {lon, lat} object format
 */
function isObjectFormat(point: unknown): point is BoundsLocation {
  return (
    typeof point === 'object' &&
    point !== null &&
    'lon' in point &&
    'lat' in point
  );
}

/**
 * Checks if coordinates are in [longitude, latitude] format vs [latitude, longitude]
 * Uses the fact that longitude ranges from -180 to 180 and latitude from -90 to 90
 * Returns true if coordinates match [lon, lat], false if they match [lat, lon]
 * Throws error if coordinates are invalid or ambiguous
 */
function isLonLatFormat(coordinates: number[]): boolean {
  const isFirstLon =
    inRange(coordinates[0], -180, 181) && !inRange(coordinates[0], -90, 91);
  const isFirstLat = inRange(coordinates[0], -90, 91);
  const isSecondLon =
    inRange(coordinates[1], -180, 181) && !inRange(coordinates[1], -90, 91);
  const isSecondLat = inRange(coordinates[1], -90, 91);

  if (isFirstLon && isSecondLat) return true; // Definitely [lon, lat]
  if (isFirstLat && isSecondLon) return false; // Definitely [lat, lon]

  throw new Error('Invalid or ambiguous coordinates');
}

/**
 * Adapt bounds to the format expected by the map.
 * @param bounds - The bounds to adapt.
 * @returns The adapted bounds.
 */
function adaptBounds(
  bounds: LeafletOrMapLibreBounds | BoundsLocation
): LngLatBounds | LatLngBounds {
  // Handle single point location (BoundsLocation) by creating a bounded area around it
  if (!Array.isArray(bounds) && 'lon' in bounds && 'lat' in bounds) {
    if (mapLibre) {
      const locationLngLat = new LngLat(bounds.lon, bounds.lat);
      return LngLatBounds.fromLngLat(locationLngLat, poiBoundsExtent);
    }
    return L.latLng(bounds).toBounds(poiBoundsExtent);
  }

  if (mapLibre) {
    // If bounds is already a MapLibre LngLatBounds, return as is
    if (bounds instanceof LngLatBounds) {
      return bounds;
    }

    // Convert Leaflet LatLngBounds to MapLibre LngLatBounds
    if (bounds instanceof L.LatLngBounds) {
      const southWest = bounds.getSouthWest();
      const northEast = bounds.getNorthEast();
      return new LngLatBounds(
        [southWest.lng, southWest.lat],
        [northEast.lng, northEast.lat]
      );
    }

    // Handle array of points (either BoundsLocation[] or coordinate pairs)
    if (Array.isArray(bounds) && bounds.length > 0) {
      const firstElement = bounds[0];
      // Detect format: either {lon, lat} objects or coordinate arrays
      const firstElementIsObjectFormat = isObjectFormat(firstElement);
      // Check if coordinates are in [longitude, latitude] format (vs [latitude, longitude])
      const firstElementIsLonLat =
        firstElementIsObjectFormat || isLonLatFormat(firstElement as number[]);

      // Create bounds that encompass all points
      const lngLatBounds = new LngLatBounds();
      bounds.forEach(point => {
        let [lng, lat] = [0, 0];
        if (firstElementIsObjectFormat) {
          // Handle {lon, lat} object format
          ({ lon: lng, lat } = point);
        } else if (firstElementIsLonLat) {
          // Handle [longitude, latitude] array format
          [lng, lat] = point;
        } else {
          // Handle [latitude, longitude] array format
          [lat, lng] = point;
        }

        // Only extend bounds if coordinates are valid numbers
        if (!Number.isNaN(lng) && !Number.isNaN(lat)) {
          lngLatBounds.extend([lng, lat]);
        } else {
          console.error('Invalid point detected:', point);
        }
      });
      return lngLatBounds;
    }

    throw new Error('Invalid bounds data provided for MapLibre mode');
  }
  // If not in MapLibre mode, return original bounds (assumed to be LatLngBounds)
  return bounds as LatLngBounds;
}

export function zoomTo(
  map: L.Map | MapRef,
  location: BoundsLocation,
  options?: FitBoundsOptions
): void {
  try {
    if (!location) {
      throw new Error('No location data provided to zoomTo');
    }

    // Convert single point location into a bounds object (Leaflet or MapLibre format)
    const adaptedBounds = adaptBounds(location);

    // Stop any ongoing animations
    map.stop();

    // Prepare options with current pitch and bearing if using MapLibre
    const fitOptions = {
      ...(mapLibre && { pitch: map.getPitch(), bearing: map.getBearing() }),
      ...options,
    };

    // Fit the bounds to the map
    map.fitBounds(adaptedBounds, fitOptions);
  } catch (error) {
    console.error('Error in zoomTo:', error);
  }
}

export function fitTo(
  map: L.Map | MapRef,
  bounds: LeafletOrMapLibreBounds,
  options?: FitBoundsOptions
): void {
  try {
    if (!bounds) {
      throw new Error('No bounds data provided to fitBounds');
    }

    // Normalize bounds input into a consistent bounds object (Leaflet or MapLibre format)
    const adaptedBounds = adaptBounds(bounds);

    // Stop any ongoing animations
    map.stop();

    // Prepare options with current pitch and bearing if using MapLibre
    const fitOptions = {
      ...(mapLibre && { pitch: map.getPitch(), bearing: map.getBearing() }),
      ...options,
    };

    // Fit the bounds to the map
    map.fitBounds(adaptedBounds, fitOptions);
  } catch (error) {
    console.error('Error in fitTo:', error);
  }
}
